/**
 * What a row's score is worth before anything else is added, and how much a
 * near date is worth on top — one decision for Browse and Today.
 *
 * No imports, and imported by `browse.ts`, `browseRank.ts`, `today.ts`,
 * `affinity.ts` and `deskBrowse.ts` with the `.ts` extension, so the root
 * suite loads it directly.
 *
 * ## A null score is "no opinion", and no opinion is the middle
 *
 * `score` is null for two different reasons, and the app cannot tell them
 * apart from the row: **unranked by design** (`notRanked` in `src/score.ts`:
 * theatre, dance, exhibitions, openings, releases — kinds the profile cannot
 * say anything about) and **not scored yet** (a lead promoted this morning;
 * the 4 Oct Design Börse sat in this state for two days). The two screens
 * read it differently: Browse's `browseRank` took null as 0.45, so did
 * `affinity.ts`'s own `effectiveScore`, but Today's restatement of that
 * function took it as 0 — *below every row the profile has scored as poor*.
 * Today put an unscored design fair under a 0.12 concert, and a day block's
 * cap cut it first; Browse put it above four scored rows in five.
 *
 * **The rule: null is `UNSCORED_RANK` everywhere a row is ordered against
 * scored rows, and `UNSCORED_RANK` is the median of the scores the profile
 * actually gives.** "No view" is neither a dislike (0) nor a recommendation;
 * it is the middle. Browse's 0.45 was written as "unscored rows sit below the
 * median" and is not: on 4 Oct 2026 the 1,815 scored upcoming rows had
 * quartiles 0.12 / 0.25 / 0.40, so 0.45 put the 1,500 unscored ones above
 * more than three in four scored ones. 0.25 is the median of that set.
 * **It is a measurement, so it goes stale**: when the scorer or the profile
 * changes, re-measure (`select percentile_cont(0.5) within group (order by
 * score) from user_entry_score where user_id = '<uid>' and score is not null`,
 * the shared `feed_lite.score` it was measured on until WP U2.3 being gone) and
 * change this one number. It is one person's median, Doug's; a person the rule
 * ranker scores has a different spread, and 0.25 is still the middle of nothing
 * in particular for them — re-measure per person before giving anyone a number
 * of their own.
 *
 * **Where `score` comes from** (WP U2.3): the signed-in person's own row in
 * `user_entry_score`, laid on every feed row by `useFeed` (`userScores.ts`). A
 * row they have no score for is null, and null is this rank.
 *
 * Two things do not change: a null never reaches `PICK_MIN` (0.7) on its own,
 * so the picks rail still shows only what was scored well; and rows that are
 * all null (a lane of exhibitions) still tie, so their order is whatever
 * their sort's tiebreak says. What does change: on Today an unranked-by-design
 * row (theatre, an opening) moves from the bottom of a section to the middle
 * of it, and in Browse an unscored row sits at the median instead of in the
 * top quartile.
 */
export const UNSCORED_RANK = 0.25

/** The score a row is ordered by, before affinity. Always a finite number:
 *  a `numeric` column can arrive as a string on some paths, so this coerces
 *  rather than trusting the type. */
export function scoreOf(e: { score?: number | string | null }): number {
  if (e.score == null) return UNSCORED_RANK
  const n = typeof e.score === 'number' ? e.score : Number(e.score)
  return Number.isFinite(n) ? n : UNSCORED_RANK
}

/** Days from one `YYYY-MM-DD` to another, at noon UTC. */
const daysDiff = (a: string, b: string) =>
  Math.round((Date.parse(`${b}T12:00:00Z`) - Date.parse(`${a}T12:00:00Z`)) / 86400000)

/** The calendar day of a moment in `tz`, or null for one that does not parse.
 *  `zone.ts`'s `dayIn`, restated because this file imports nothing (see
 *  above), with the formatter built once per zone: `urgencyLift` is asked of
 *  every row on every render. Exported for `browseRank.ts`, which imports
 *  this file and nothing else, so the ranking files share one copy. */
const dayFmts = new Map<string, Intl.DateTimeFormat>()
export function dayOf(at: string | Date, tz: string): string | null {
  const d = new Date(at)
  if (Number.isNaN(d.getTime())) return null
  let f = dayFmts.get(tz)
  if (!f) dayFmts.set(tz, f = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: 'numeric', day: 'numeric' }))
  return f.format(d)
}

/** How near is "soon" — on now, or starting inside this many days. */
export const SOON_DAYS = 3

/** The size of each urgency, in rank. Mild by design: Browse says what is
 *  good, and time is Today's job (`browseRank.ts`'s own header). */
export const URGENCY_CLOSING = 0.06
export const URGENCY_SOON = 0.06
export const URGENCY_NEAR = 0.04

/** The longest run, in inclusive days, still counted as "on now" when it
 *  covers today. A film week, a weekend of shows or a fair is an event you
 *  can be at today; a three-month exhibition is on every day of its life and
 *  lifting all of them uniformly would say nothing. Same number as
 *  `daylong.ts`'s `SPAN_MAX_DAYS`, counted the same way. */
export const ON_NOW_MAX_DAYS = 14

export interface UrgencyRow {
  run_start?: string | null
  run_end?: string | null
  next_showing?: string | null
  next_opening?: string | null
}

/**
 * The one urgency a row earns on `today` (the current region's `YYYY-MM-DD`), or 0.
 *
 * The larger of three, never their sum — they are three views of one fact,
 * "the date is close", and adding them would double-count it:
 *
 *  - **closing**: a run ends today or inside a week (0–7 days), +0.06. Was
 *    already there.
 *  - **soon**: it is on now, or its first occurrence is within `SOON_DAYS`
 *    days, +0.06. *On now* means a short run (`ON_NOW_MAX_DAYS`) that covers
 *    today; *first occurrence* is the earliest of a showing, an opening, or
 *    a run's start that is not yet behind.
 *  - **near**: its next showing is up to 14 days out, +0.04. This was the
 *    `else` of the `run_end` branch, and so never applied to a row that has
 *    a `run_end` — every newsletter row and most multi-night productions —
 *    which is how a festival on today sat behind a programme that ran
 *    9–31 October.
 *
 * "Next showing" is read as given: it is the view's own answer, computed
 * server-side when the view last ran, so it can be a few hours stale. A past
 * one earns nothing.
 *
 * `today` is the current region's day; `tz` is the row's own zone (C2.1),
 * which its showing and opening are read in.
 */
export function urgencyLift(e: UrgencyRow, today: string, tz: string): number {
  let lift = 0

  if (e.run_end) {
    const toEnd = daysDiff(today, e.run_end)
    if (toEnd >= 0 && toEnd <= 7) lift = Math.max(lift, URGENCY_CLOSING)
  }

  // Days until each thing a person could go to.
  const ahead: number[] = []
  let nextShowingIn: number | null = null
  for (const [key, at] of [['showing', e.next_showing], ['opening', e.next_opening]] as const) {
    const day = at ? dayOf(at, tz) : null
    if (!day) continue
    const d = daysDiff(today, day)
    ahead.push(d)
    if (key === 'showing') nextShowingIn = d
  }
  if (e.run_start && e.run_start >= today) ahead.push(daysDiff(today, e.run_start))
  if (e.run_start && e.run_end && e.run_start <= today && e.run_end >= today
      && daysDiff(e.run_start, e.run_end) + 1 <= ON_NOW_MAX_DAYS) ahead.push(0)

  if (ahead.some(d => d >= 0 && d <= SOON_DAYS)) lift = Math.max(lift, URGENCY_SOON)
  if (nextShowingIn != null && nextShowingIn >= 0 && nextShowingIn <= 14) lift = Math.max(lift, URGENCY_NEAR)
  return lift
}
